import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const SITE_KEY = "0x4AAAAAAEHK_17ccyBESjci";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

let scriptPromise = null;
function loadScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) return resolve(window.turnstile);
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.turnstile);
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

// Renders a Turnstile widget and reports fresh tokens up via onToken.
// By default it's an always-visible inline widget (matches anyainow's
// LoginClient: size "flexible", full width) so it sits predictably in the
// form's own layout instead of floating in a fixed corner. Pass
// invisible={true} for flows that need to stay frictionless — Cloudflare
// then only shows UI if it decides a challenge is actually needed (the
// anonymous scan preview relies on this). Tokens are single-use; call
// ref.reset() after a successful submit to get a fresh one for the next
// attempt.
const Turnstile = forwardRef(function Turnstile({ onToken, action, invisible }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadScript().then((turnstile) => {
      if (cancelled || !containerRef.current || widgetIdRef.current || !turnstile) return;
      widgetIdRef.current = turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        action,
        appearance: invisible ? "interaction-only" : "always",
        size: invisible ? "normal" : "flexible",
        theme: "auto",
        callback: onToken,
        "expired-callback": () => onToken(null),
        "error-callback": () => onToken(null),
      });
    });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    reset() {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  return (
    <div
      ref={containerRef}
      className="cf-turnstile"
      data-sitekey={SITE_KEY}
      data-action="turnstile-spin-v2"
      style={invisible ? undefined : { width: "100%" }}
    />
  );
});

export default Turnstile;
